/**
 * @function throttle
 * @description
 * Execute a callback every X miliseconds instead of calling it repeatedly.
 * `throttle` is used for performance reasons, for example scroll events
 * that are fired very often, or key events.
 *
 * ```js
 * import throttle from 'dsjs/src/utils/throttle';
 *
 * document.addEventListener('scroll', throttle(onScroll, 50));
 *
 * function onScroll() {
 *     // do something on scroll
 * }
 * ```
 *
 * @param {Function} fn - The callback to throttle
 * @param {Number} delay - A positive integer representing the time between 2
 * calls of `fn`
 * @returns {Function} The throttled callback
 */
export default function(fn, delay) {
    var context, timeout, result, args, diff;
    var prevCall = 0;

    function delayed() {
        prevCall = Date.now();
        timeout = null;
        result = fn.apply(context, args);
    }

    function throttled() {
        context = this;
        args = arguments;
        diff = delay - (Date.now() - prevCall);

        if(diff <= 0) {
            clearTimeout(timeout);
            delayed();
        } else if(! timeout) {
            timeout = setTimeout(delayed, diff);
        }

        return result;
    }

    throttled.cancel = function() {
        clearTimeout(timeout);
    };

    return throttled;
}